// src/lib/idb-wrapper.ts
// IndexedDB helper wrapped over idb-keyval with corruption recovery.
//
// Lazy init: importing this module is synchronous. First IDB call awaits
// initIDB() which creates the DB, ensures stores exist, and caches handles.

import {
  get as idbKvGet,
  set as idbKvSet,
  del as idbKvDel,
  keys as idbKvKeys,
  createStore,
  type UseStore,
} from 'idb-keyval';

export const DB_NAME = 'espforsports';
export const STORE_PROGRESS = 'progress';
export const STORE_QUEUE = 'syncQueue';
export const STORE_META = 'meta';

export type StoreName = typeof STORE_PROGRESS | typeof STORE_QUEUE | typeof STORE_META;

let progressStore: UseStore | null = null;
let queueStore: UseStore | null = null;
let metaStore: UseStore | null = null;

let _initPromise: Promise<void> | null = null;

/** Idempotent init. All exported helpers call this internally. */
export function initIDB(): Promise<void> {
  if (!_initPromise) _initPromise = _doInit();
  return _initPromise;
}

async function _doInit(): Promise<void> {
  await _ensureStores(DB_NAME, [STORE_PROGRESS, STORE_QUEUE, STORE_META]);
  progressStore = createStore(DB_NAME, STORE_PROGRESS);
  queueStore = createStore(DB_NAME, STORE_QUEUE);
  metaStore = createStore(DB_NAME, STORE_META);
}

async function _ensureStores(dbName: string, storeNames: string[]): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.open(dbName, 2);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of storeNames) {
        if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      const missing = storeNames.some(s => !db.objectStoreNames.contains(s));
      db.close();
      if (!missing) return resolve();

      // Loud warning before destructive recovery (BUG 19 fix preserved).
      console.error(
        '[IDB] CRITICAL: Object stores missing despite DB version ≥ 2. ' +
          'Deleting and recreating database. All offline progress and cached data will be lost.'
      );
      try {
        document.dispatchEvent(
          new CustomEvent('idb:reset', {
            bubbles: true,
            detail: { reason: 'missing-stores', dbName },
          })
        );
      } catch {
        /* document may be unavailable in some envs */
      }

      const delReq = indexedDB.deleteDatabase(dbName);
      delReq.onerror = () => reject(delReq.error);
      delReq.onsuccess = () => resolve();
    };
  });
}

function _pickStore(storeName: StoreName): UseStore {
  if (storeName === STORE_QUEUE) return queueStore!;
  if (storeName === STORE_META) return metaStore!;
  return progressStore!;
}

// ── Key helpers ─────────────────────────────────────────────────────────────
export const progressKey = (uidOrAnon: string, unitId: string): string =>
  `progress:${uidOrAnon}:${unitId}`;
export const progressIndexKey = (uidOrAnon: string): string =>
  `progressIndex:${uidOrAnon}`;
export const queueItemsKey = (): string => 'queue:items';
export const metaKey = (k: string): string => `meta:${k}`;

// ── Generic store-aware helpers ─────────────────────────────────────────────
export async function idbGet<T = unknown>(
  key: string,
  storeName: StoreName = STORE_PROGRESS
): Promise<T | undefined> {
  await initIDB();
  return idbKvGet<T>(key, _pickStore(storeName));
}

export async function idbSet<T = unknown>(
  key: string,
  value: T,
  storeName: StoreName = STORE_PROGRESS
): Promise<void> {
  await initIDB();
  return idbKvSet(key, value, _pickStore(storeName));
}

export async function idbDel(
  key: string,
  storeName: StoreName = STORE_PROGRESS
): Promise<void> {
  await initIDB();
  return idbKvDel(key, _pickStore(storeName));
}

export async function idbKeys(
  storeName: StoreName = STORE_PROGRESS
): Promise<IDBValidKey[]> {
  await initIDB();
  return idbKvKeys(_pickStore(storeName));
}

// ── Unit index helpers ──────────────────────────────────────────────────────
export async function addUnitToIndex(uidOrAnon: string, unitId: string): Promise<void> {
  const k = progressIndexKey(uidOrAnon);
  const idx = (await idbGet<string[]>(k, STORE_META)) || [];
  if (!idx.includes(unitId)) {
    idx.push(unitId);
    await idbSet(k, idx, STORE_META);
  }
}

export async function getUnitIndex(uidOrAnon: string): Promise<string[]> {
  return (await idbGet<string[]>(progressIndexKey(uidOrAnon), STORE_META)) || [];
}